// src/context/LeaveContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./AuthContext";

// ── Context ────────────────────────────────────
const LeaveContext = createContext(null);

export function useLeave() {
  const ctx = useContext(LeaveContext);
  if (!ctx) throw new Error("useLeave must be used inside <LeaveProvider>");
  return ctx;
}

// ── Unified status flow ────────────────────────
// PENDING_LEAD   → employee submitted, awaiting team lead
// PENDING_ADMIN  → lead approved (or no lead), awaiting admin
// APPROVED       → admin approved ✅
// REJECTED       → rejected at any stage ❌

// ── Provider ───────────────────────────────────
async function createNotification({
  title,
  message,
  recipientId,
  type = "leave",
  actionUrl = "",
  createdBy = "",
  metadata = {},
}) {
  await addDoc(collection(db, "notifications"), {
    title,
    message,
    recipientId,
    recipientType: "user",
    type,
    priority: "normal",
    actionUrl,
    createdBy,
    metadata,
    readBy: [],
    createdAt: serverTimestamp(),
  });
}




export function LeaveProvider({ children }) {
  const { currentUser, loading: authLoading } = useAuth();

  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  const [todayApprovedLeavesCount, setTodayApprovedLeavesCount] = useState(0);
  const [todayApprovedLeavesLoading, setTodayApprovedLeavesLoading] = useState(true);

  const [allRequests, setAllRequests] = useState([]);
  const [allRequestsLoading, setAllRequestsLoading] = useState(true);

  const [teamRequests, setTeamRequests] = useState([]);
  const [teamRequestsLoading, setTeamRequestsLoading] = useState(true);

  const [userRequests, setUserRequests] = useState([]);
  const [userRequestsLoading, setUserRequestsLoading] = useState(true);

  // ── Pending listener — pending_lead + pending_admin ──
  useEffect(() => {
    if (authLoading || !currentUser) {
      setPendingRequests([]);
      setPendingLoading(false);
      return;
    }

    setPendingLoading(true);
    const q = query(
      collection(db, "leaveRequests"),
      where("status", "in", ["pending_lead", "pending_admin"]),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPendingRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPendingLoading(false);
      },
      (err) => {
        console.error("[LeaveContext:pendingRequests]", err);
        setPendingLoading(false);
      }
    );
    return unsub;
  }, [authLoading, currentUser]);

  // ── Today approved leaves counter ──
  useEffect(() => {
    if (authLoading || !currentUser) {
      setTodayApprovedLeavesCount(0);
      setTodayApprovedLeavesLoading(false);
      return;
    }

    setTodayApprovedLeavesLoading(true);
    const today = new Date().toISOString().split("T")[0];
    const q = query(
      collection(db, "leaveRequests"),
      where("status", "==", "approved"),
      where("fromDate", "<=", today),
      where("toDate", ">=", today),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTodayApprovedLeavesCount(snap.size);
        setTodayApprovedLeavesLoading(false);
      },
      (err) => {
        console.error("[LeaveContext:todayApprovedLeavesCount]", err);
        setTodayApprovedLeavesLoading(false);
      }
    );
    return unsub;
  }, [authLoading, currentUser]);

  // ── All requests — realtime for admin ──
  useEffect(() => {
    if (authLoading || !currentUser) {
      setAllRequests([]);
      setAllRequestsLoading(false);
      return;
    }

    setAllRequestsLoading(true);
    const q = query(
      collection(db, "leaveRequests"),
      orderBy("createdAt", "desc"),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAllRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAllRequestsLoading(false);
      },
      (err) => {
        console.error("[LeaveContext:allRequests]", err);
        setAllRequestsLoading(false);
      }
    );
    return unsub;
  }, [authLoading, currentUser]);

  // ── Team requests — ONLY pending_lead (lead's action queue) ──
  const subscribeToTeamRequests = useCallback((leadId) => {
    if (!leadId) {
      setTeamRequests([]);
      setTeamRequestsLoading(false);
      return () => {};
    }
    setTeamRequestsLoading(true);
    const q = query(
      collection(db, "leaveRequests"),
      where("teamLeadId", "==", leadId),
      where("status", "==", "pending_lead"),
      orderBy("createdAt", "desc"),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTeamRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTeamRequestsLoading(false);
      },
      (err) => {
        console.error("[LeaveContext:teamRequests]", err);
        setTeamRequestsLoading(false);
      }
    );
    return unsub;
  }, []);

  // ── User requests ──
  const subscribeToUserRequests = useCallback((userId) => {
    if (!userId) {
      setUserRequests([]);
      setUserRequestsLoading(false);
      return () => {};
    }
    setUserRequestsLoading(true);
    const q = query(
      collection(db, "leaveRequests"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setUserRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setUserRequestsLoading(false);
      },
      (err) => {
        console.error("[LeaveContext:userRequests]", err);
        setUserRequestsLoading(false);
      }
    );
    return unsub;
  }, []);

  // ── Team Lead review ──
  // approve → pending_admin | reject → rejected
  const reviewLeaveRequest = useCallback(
    async (requestId, action, reviewerId) => {
      const request = teamRequests.find((r) => r.id === requestId);
      if (!request) throw new Error("Request not found or already reviewed");
      if (request.status !== "pending_lead") throw new Error("This request has already been reviewed");

      const newStatus = action === "approve" ? "pending_admin" : "rejected";

      // Optimistic: remove from pending list immediately
      setTeamRequests((prev) => prev.filter((r) => r.id !== requestId));
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));

      try {
        const updates = {
          status: newStatus,
          reviewedBy: reviewerId,
          updatedAt: serverTimestamp(),
        };
        if (action === "approve") {
          updates.leadApprovedBy = reviewerId;
          updates.leadApprovedAt = serverTimestamp();
        } else {
          updates.rejectedBy = reviewerId;
          updates.rejectedAt = serverTimestamp();
        }
        await updateDoc(doc(db, "leaveRequests", requestId), updates);
        if (action === "approve") {
  await createNotification({
    title: "Leave Sent for Admin Approval",
    message: "Your leave request has been approved by your Team Lead and sent to Admin.",
    recipientId: request.userId,
    createdBy: reviewerId,
    actionUrl: "/employee/leave",
    metadata: { leaveId: requestId },
  });
} else {
  await createNotification({
    title: "Leave Rejected",
    message: "Your Team Lead rejected your leave request.",
    recipientId: request.userId,
    createdBy: reviewerId,
    actionUrl: "/employee/leave",
    metadata: { leaveId: requestId },
  });
}
      } catch (error) {
        // Rollback
        setTeamRequests((prev) => [request, ...prev]);
        setPendingRequests((prev) => [request, ...prev]);
        throw error;
      }
    },
    [teamRequests],
  );

  // ── Admin review ──
  // Handles both pending_admin AND pending_lead (direct/no-lead requests)
  // approve → approved | reject → rejected
  const adminReviewLeaveRequest = useCallback(
    async (requestId, action, adminId) => {
      const request = allRequests.find((r) => r.id === requestId);
      if (!request) throw new Error("Request not found");

      const actionable = ["pending_admin", "pending_lead"];
      if (!actionable.includes(request.status)) {
        throw new Error("This request has already been finalised");
      }

      const newStatus = action === "approve" ? "approved" : "rejected";

      // Optimistic update
      setAllRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: newStatus } : r)),
      );
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));

      try {
        const updates = {
          status: newStatus,
          reviewedBy: adminId,
          updatedAt: serverTimestamp(),
        };
        if (action === "approve") {
          updates.approvedBy = adminId;
          updates.approvedAt = serverTimestamp();
          updates.rejectedBy = null;
          updates.rejectedAt = null;
        } else {
          updates.rejectedBy = adminId;
          updates.rejectedAt = serverTimestamp();
          updates.approvedBy = null;
          updates.approvedAt = null;
        }
        
        await updateDoc(doc(db, "leaveRequests", requestId), updates);
        await createNotification({
  title: action === "approve"
    ? "Leave Approved"
    : "Leave Rejected",

  message: action === "approve"
    ? "Your leave request has been approved."
    : "Your leave request has been rejected.",

  recipientId: request.userId,
  createdBy: adminId,
  actionUrl: "/employee/leave",
  metadata: {
    leaveId: requestId,
  },
});
      } catch (error) {
        // Rollback
        setAllRequests((prev) =>
          prev.map((r) => (r.id === requestId ? request : r)),
        );
        setPendingRequests((prev) => [request, ...prev]);
        throw error;
      }
    },
    [allRequests],
  );

  const value = React.useMemo(
    () => ({
      pendingRequests,
      pendingLoading,
      allRequests,
      allRequestsLoading,
      teamRequests,
      teamRequestsLoading,
      subscribeToTeamRequests,
      userRequests,
      userRequestsLoading,
      subscribeToUserRequests,
      todayApprovedLeavesCount,
      todayApprovedLeavesLoading,
      reviewLeaveRequest,
      adminReviewLeaveRequest,
    }),
    [
      pendingRequests,
      pendingLoading,
      allRequests,
      allRequestsLoading,
      todayApprovedLeavesCount,
      todayApprovedLeavesLoading,
      teamRequests,
      teamRequestsLoading,
      subscribeToTeamRequests,
      userRequests,
      userRequestsLoading,
      subscribeToUserRequests,
      reviewLeaveRequest,
      adminReviewLeaveRequest,
    ],
  );

  return (
    <LeaveContext.Provider value={value}>{children}</LeaveContext.Provider>
  );
}
